import { useCallback, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from '@hello-pangea/dnd';
import { DragHandle } from '@mui/icons-material';
import { Box, Button, List, ListItem, Typography } from '@mui/material';
import { BaseDialog } from '@/components/shared/base-dialog';
import { useNotification } from '@/context/notification';
import { useManagePath } from '@/packages/learning-path/pages/path-management/pages/edit-path/context';
import type { ContentBlock } from '@/packages/learning-path/pages/path-management/pages/edit-path/context/types';

interface ReorderBlocksDialogProps {
  onClose: () => void;
}

export const ReorderBlocksDialog = ({ onClose }: ReorderBlocksDialogProps) => {
  const { contents, reorderBlocks } = useManagePath();
  const { toast } = useNotification();
  const [items, setItems] = useState<ContentBlock[]>(contents);

  const handleSave = () => {
    reorderBlocks(items);
    toast({
      content: 'Ordenação salva com sucesso!',
      type: 'success',
    });
    onClose();
  };

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const next = [...items];
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      setItems(
        next.map((item, index) => ({
          ...item,
          position: index,
        }))
      );
    },
    [items]
  );

  return (
    <BaseDialog
      onClose={onClose}
      open
      fullWidth
      maxWidth="sm"
      title={
        <Typography>
          Reordenar conteúdos
          <Typography variant="body2" color="text.secondary">
            Altere a ordem dos conteúdos, arrastando-os.
          </Typography>
        </Typography>
      }
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="contents">
          {(provided) => (
            <List ref={provided.innerRef} {...provided.droppableProps}>
              {items.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(providedDraggable, snapshot) => (
                    <ListItem
                      ref={providedDraggable.innerRef}
                      {...providedDraggable.draggableProps}
                      {...providedDraggable.dragHandleProps}
                      secondaryAction={<DragHandle />}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: snapshot.isDragging
                          ? 'action.hover'
                          : 'background.paper',
                        cursor: 'grab',
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {`${index + 1}. ${
                          item.type === 'text'
                            ? item.content.body?.trim() || 'Texto vazio'
                            : 'Seção de recursos'
                        }`}
                      </Typography>
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>

      <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
        <Button variant="text" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Salvar
        </Button>
      </Box>
    </BaseDialog>
  );
};
